import { Command, Option } from "clipanion";
import { M8tCommand } from "../../lib/m8t-command.js";
import { confirm } from "@inquirer/prompts";
import { apiCall } from "../../lib/api-client.js";
import { LocalCliError } from "../../lib/errors.js";
import { resolveGatewayContext } from "../../lib/gateway-context.js";
import { colors, renderJson, resolveOutputMode } from "../../lib/output.js";

export class TeamRemoveCommand extends M8tCommand {
  static paths = [["team", "remove"]];
  static usage = Command.Usage({
    description: "Remove a team member entirely.",
    details:
      "Prompts for confirmation by default. Pass --yes to skip. Removes ALL channel identity rows for the handle.",
    examples: [
      ["With confirmation prompt", "$0 team remove idabest"],
      ["Skip confirmation", "$0 team remove idabest --yes"],
    ],
  });

  handle = Option.String();
  yes = Option.Boolean("--yes", false, { description: "Skip confirmation prompt." });
  output = Option.String("--output");
  subscription = Option.String("--subscription", {
    description: "Azure subscription ID to discover gateway in (defaults to active az subscription).",
  });
  resourceGroup = Option.String("--resource-group", {
    description: "m8t-stack resource group to disambiguate the gateway (multi-deployment subscriptions).",
  });

  async executeCommand(): Promise<number> {
    const mode = resolveOutputMode(
      this.output as "pretty" | "json" | "auto" | undefined,
      this.context.stdout as NodeJS.WriteStream,
    );
    const interactive = (this.context.stdout as NodeJS.WriteStream).isTTY === true;

    if (!this.yes) {
      if (!interactive) {
        throw new LocalCliError({
          code: "USAGE",
          message: "remove requires --yes when running non-interactively.",
          hint: `m8t team remove ${this.handle} --yes`,
        });
      }
      const ok = await confirm({
        message: `Remove team member '${this.handle}' entirely?`,
        default: false,
      });
      if (!ok) {
        if (mode === "pretty") this.context.stdout.write("cancelled.\n");
        return 2;
      }
    }

    const ctx = await resolveGatewayContext({ interactive, subscriptionId: this.subscription, resourceGroup: this.resourceGroup });
    const result = await apiCall<{ deleted: { handle: string; rows: number } }>(
      { gatewayUrl: ctx.gatewayUrl, gatewayClientId: ctx.gatewayClientId },
      { method: "DELETE", path: `/api/team/${encodeURIComponent(this.handle)}` },
      (msg) => this.context.stderr.write(msg + "\n"),
    );

    if (mode === "json") {
      this.context.stdout.write(renderJson(result) + "\n");
      return 0;
    }

    this.context.stdout.write(
      `${colors.success("✓")} removed ${colors.field(result.deleted.handle)} (${result.deleted.rows.toString()} row${result.deleted.rows === 1 ? "" : "s"}).\n`,
    );
    return 0;
  }
}
